import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, vModelCheckbox as _vModelCheckbox, normalizeClass as _normalizeClass, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  class: "fixed inset-0 z-50 overflow-y-auto",
  "data-dusk": "add-patient-modal"
}
const _hoisted_2 = { class: "flex items-stretch justify-center text-left min-h-screen m-0 p-0" }
const _hoisted_3 = { class: "relative align-top bg-white rounded-lg shadow-xl w-full max-w-5xl z-50" }
const _hoisted_4 = { class: "p-10" }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "max-w-4xl p-10 mx-auto bg-white border rounded-lg z-50" }
const _hoisted_7 = { class: "mb-2 text-lg font-semibold text-gray-900" }
const _hoisted_8 = { class: "mb-6 text-sm font-normal text-gray-500" }
const _hoisted_9 = { class: "grid grid-cols-1 md:grid-cols-2" }
const _hoisted_10 = { class: "max-w-4xl p-10 mt-10 mx-auto bg-white border rounded-lg" }
const _hoisted_11 = {
  key: 0,
  class: "inline-flex items-center h-8 font-medium text-gray-900 border rounded"
}
const _hoisted_12 = {
  "data-dusk": "org-title",
  class: "px-4"
}
const _hoisted_13 = {
  key: 1,
  class: "max-w-5xl mx-auto overflow-hidden bg-white border rounded-lg"
}
const _hoisted_14 = { class: "relative" }
const _hoisted_15 = { class: "p-10 overflow-y-scroll max-h-125" }
const _hoisted_16 = { class: "px-10 py-6 border-t" }
const _hoisted_17 = { class: "flex items-center" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "mt-1 ml-3" }
const _hoisted_20 = { class: "px-10 py-6 border-t" }
const _hoisted_21 = { class: "flex" }
const _hoisted_22 = { class: "ml-3" }

import { computed, defineAsyncComponent, onBeforeMount, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import axios, { AxiosRequestConfig } from 'axios';
import {
  BaseButton,
  BaseTextInput
} from '@/lib/components';
import WorkflowLayout from '@/lib/layouts/WorkflowLayout.vue';
import {
  CustomerBranding,
  PaginatedResponse,
  Patient
} from '@/models';
import { OrganisationPatientService } from '@/services/api';
import { verifyMrn } from '@/helpers/patient.helper';
import { MRN_MAX_LENGTH } from '@/constants/patients';
import { useSessionStore } from '@/stores/session.store';
import { useNotificationStore } from '@/stores/notification.store';
import PatientDetailsForm from '@/views/patient/components/PatientDetailsForm.vue';
import { countErrors } from '@/custom/menicon/helpers/validators.helper';


export default /*@__PURE__*/_defineComponent({
  __name: 'AddPatientModal',
  props: {
  organisationId: {
    type: String,
    required: true
  }
},
  emits: ['close', 'success'],
  setup(__props, { emit: __emit }) {

const BaseSelectDropdown = defineAsyncComponent(() => import('@/lib/components/Select/BaseSelectDropdown.vue'));
const LegalPolicy = defineAsyncComponent(() => import('@/lib/components/LegalPolicy/LegalPolicy.vue'));

const { t } = useI18n();
const emit = __emit;

const props = __props;

const patientData = ref(null);
const patientForm = ref(null);

const sessionStore = useSessionStore();
const notificationStore = useNotificationStore();
const saving = ref(false);
const step = ref(0);
const steps = ref<string[]>([t('platform.patient.details')]);
const mrn = ref<string>('');
const acceptTerms = ref(true);
const consentToMedicalResearch = ref(false);
const policyFetched = ref(true);
const errors = ref<Record<string, string[]>>({});
const controller = new AbortController();
const selectedRegionId = ref<string | null>(null);
const patient = ref<Patient | null>(null);
const shouldValidateMrn = ref<boolean>(true);
const isValidating = ref<boolean>(false);

const isMenicon = computed<boolean>(() => sessionStore.isCustomer(CustomerBranding.MENICON));
const isFormValid = computed<boolean>(() => Boolean(patientForm.value?.isFormValid));
const allLegalRegions = computed<string[]>(() => sessionStore.legalRegions);
const branding = computed(() => [sessionStore.customerBranding]);
const legalregionsDropdownOptions = computed(() => allLegalRegions.value.map((region) => ({
  value: region,
  label: t(`platform.countries.${region.toUpperCase()}`)
})));
const orgPatientService = computed(() => new OrganisationPatientService(props.organisationId));
const mrnExists = computed<boolean>(() => !!mrn.value?.length);

onBeforeMount(() => {
  document.body.classList.add('overflow-hidden');
  selectedRegionId.value = allLegalRegions.value[0];
  if (isMenicon.value) {
    steps.value = [
      t('platform.patient.details') as string,
      t('platform.legal.privacy-policy.title') as string
    ];
    acceptTerms.value = false;
    policyFetched.value = false;
  }
});

onBeforeUnmount(() => {
  controller.abort();
  document.body.classList.remove('overflow-hidden');
});

const back = () => {
  if (step.value === 0) {
    emit('close');
    return;
  }
  delete errors.value.accept_terms;
  step.value--;
};

const next = async () => {
  await validateForm();
  if (disableNext.value) {
    return;
  }
  patientData.value = patientForm.value?.patientData;
  step.value++;
};

const complete = async () => {
  await validateForm();
  if (disableNext.value) {
    return;
  }

  saving.value = true;
  try {
    const newPatientData: Partial<Patient> = {
      ...patientData.value ?? patientForm.value?.patientData,
      accept_terms: acceptTerms.value,
      consent_to_medical_research: consentToMedicalResearch.value
    };
    if (mrn.value) {
      newPatientData.mrn = mrn.value;
    }

    patient.value = (await orgPatientService.value.create(newPatientData)).data;

    notificationStore.addSuccessNotification({ title: t('platform.patient.added') });
    emit('success', patient.value);
    emit('close');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 422) {
        errors.value = error.response.data.errors;
        step.value = 0;
      } else {
        notificationStore.addErrorNotification({
          title: t('platform.patient.saving-error'),
          label: error.response?.data?.message ?? null
        });
      }
    } else {
      throw error;
    }
  } finally {
    saving.value = false;
  }
};

const validateMrn = async (mrn: string) => {
  isValidating.value = true;
  if (mrn.length) {
    if (!verifyMrn(mrn)) {
      errors.value = { ...errors.value, mrn: [t('platform.error.spaces-not-allowed')] };
      shouldValidateMrn.value = false;
      isValidating.value = false;
      return;
    }
    const requestConfig: AxiosRequestConfig = {
      params: {
        include: 'patient',
        'filter[mrn]': mrn,
        'filter[trashed]': 'with'
      },
      signal: controller.signal
    };
    const response = (await orgPatientService.value.index(requestConfig)) as PaginatedResponse<Patient[]>;

    if (response.data.length) {
      errors.value = { ...errors.value, mrn: [t('platform.patient.mrn-error')] };
      shouldValidateMrn.value = false;
      isValidating.value = false;
      return;
    }
    onMrnInput(mrn);
  } else {
    errors.value = { ...errors.value, mrn: [t('platform.error.required')] };
  }
  shouldValidateMrn.value = false;
  isValidating.value = false;
};

const onMrnInput = (value: string) => {
  shouldValidateMrn.value = true;
  if (value.length > MRN_MAX_LENGTH) {
    errors.value = {
      ...errors.value,
      mrn: [t('platform.error.maximum-character', [MRN_MAX_LENGTH])]
    };
  } else {
    delete errors.value?.mrn;
  }
};

const validateForm = async () => {
  if (step.value === 0) {
    patientForm.value?.validateForm();
    if (shouldValidateMrn.value) {
      await validateMrn(mrn.value);
    }
  } else if (!acceptTerms.value) {
    errors.value = {
      accept_terms: [t('platform.error.required')]
    };
  }
};

const disableNext = computed<boolean>(() => {
  if (step.value === 0) {
    return !isFormValid.value || !mrnExists.value || countErrors(errors.value) > 0;
  }
  if (step.value === 1) {
    return !acceptTerms.value || !policyFetched.value;
  }
  return false;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        class: "fixed inset-0 bg-gray-500 bg-opacity-75",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close')))
      }),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(WorkflowLayout, {
          name: _ctx.$t('platform.patient.add'),
          steps: steps.value,
          "current-step": step.value,
          disabled: saving.value,
          onComplete: complete,
          onCancel: _cache[10] || (_cache[10] = ($event: any) => (emit('close')))
        }, {
          actions: _withCtx(() => [
            _createVNode(_unref(BaseButton), {
              "data-dusk": "back-btn",
              size: "large",
              color: "ghost",
              disabled: saving.value,
              onClick: back
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(step.value === 0 ? _ctx.$t('platform.common.cancel') : _ctx.$t('platform.common.back')), 1)
              ]),
              _: 1
            }, 8, ["disabled"]),
            (step.value < steps.value.length - 1)
              ? (_openBlock(), _createBlock(_unref(BaseButton), {
                  key: 0,
                  "data-dusk": "save-patient-btn",
                  size: "large",
                  class: "ml-2",
                  "right-icon": "arrow-right",
                  loading: isValidating.value,
                  onClick: _withModifiers(next, ["prevent"])
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('platform.common.save-next')), 1)
                  ]),
                  _: 1
                }, 8, ["loading"]))
              : (_openBlock(), _createBlock(_unref(BaseButton), {
                  key: 1,
                  "data-dusk": "complete-button",
                  size: "large",
                  color: "success",
                  class: "ml-2",
                  "right-icon": "arrow-right",
                  loading: saving.value || isValidating.value,
                  onClick: complete
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('platform.common.complete')), 1)
                  ]),
                  _: 1
                }, 8, ["loading"]))
          ]),
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              (step.value === 0)
                ? (_openBlock(), _createElementBlock("section", _hoisted_5, [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("h1", _hoisted_7, _toDisplayString(_ctx.$t('platform.patient.primary-id')), 1),
                      _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$t('platform.patient.primary-id-description')), 1),
                      _createElementVNode("div", _hoisted_9, [
                        _createVNode(_unref(BaseTextInput), {
                          modelValue: mrn.value,
                          "onUpdate:modelValue": [
                            _cache[1] || (_cache[1] = ($event: any) => ((mrn).value = $event)),
                            _cache[2] || (_cache[2] = ($event: any) => (onMrnInput($event as string)))
                          ],
                          count: _unref(MRN_MAX_LENGTH),
                          "data-dusk": "mrn-input",
                          error: errors.value.mrn?.[0],
                          onBlur: _cache[3] || (_cache[3] = ($event: any) => (validateMrn(mrn.value)))
                        }, null, 8, ["modelValue", "count", "error"])
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_10, [
                      _createVNode(PatientDetailsForm, {
                        ref_key: "patientForm",
                        ref: patientForm,
                        errors: errors.value,
                        "onUpdate:errors": _cache[4] || (_cache[4] = ($event: any) => ((errors).value = $event)),
                        patient: patientData.value
                      }, {
                        default: _withCtx(() => [
                          (_unref(sessionStore).currentOrganisation)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                _createElementVNode("div", _hoisted_12, _toDisplayString(_unref(sessionStore).currentOrganisation.name), 1)
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }, 8, ["errors", "patient"])
                    ])
                  ]))
                : _createCommentVNode("", true),
              (isMenicon.value && step.value === 1)
                ? (_openBlock(), _createElementBlock("section", _hoisted_13, [
                    _createElementVNode("div", _hoisted_14, [
                      _createElementVNode("div", _hoisted_15, [
                        (allLegalRegions.value.length > 1)
                          ? (_openBlock(), _createBlock(_unref(BaseSelectDropdown), {
                              key: 0,
                              modelValue: selectedRegionId.value,
                              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((selectedRegionId).value = $event)),
                              class: "max-w-xs mb-10",
                              options: legalregionsDropdownOptions.value,
                              placeholder: _ctx.$t('platform.legal.country-dropdown.placeholder')
                            }, null, 8, ["modelValue", "options", "placeholder"]))
                          : _createCommentVNode("", true),
                        (_openBlock(), _createBlock(_unref(LegalPolicy), {
                          key: selectedRegionId.value,
                          "country-code": selectedRegionId.value,
                          branding: branding.value,
                          type: "privacy-policy",
                          "force-links-new-tab": "",
                          onFetchSuccess: _cache[6] || (_cache[6] = ($event: any) => (policyFetched.value = true))
                        }, null, 8, ["country-code", "branding"]))
                      ]),
                      _cache[11] || (_cache[11] = _createElementVNode("div", {
                        class: "absolute bottom-0 left-0 right-0 h-10 mx-10",
                        style: {"background":"linear-gradient(rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%)"}
                      }, null, -1))
                    ]),
                    _createElementVNode("div", _hoisted_16, [
                      _createElementVNode("label", _hoisted_17, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((acceptTerms).value = $event)),
                          type: "checkbox",
                          disabled: !policyFetched.value,
                          class: _normalizeClass(["form-checkbox", { 'border-danger-600 outline-danger-600': errors.value.accept_terms?.[0] }]),
                          "data-dusk": "accept-terms",
                          onInput: _cache[8] || (_cache[8] = ($event: any) => (delete errors.value.accept_terms))
                        }, null, 42, _hoisted_18), [
                          [_vModelCheckbox, acceptTerms.value]
                        ]),
                        _createElementVNode("span", _hoisted_19, _toDisplayString(_ctx.$t('platform.legal.privacy-policy.accept-terms')), 1)
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_20, [
                      _createElementVNode("label", _hoisted_21, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((consentToMedicalResearch).value = $event)),
                          type: "checkbox",
                          class: "form-checkbox",
                          "data-dusk": "medical-research-consent"
                        }, null, 512), [
                          [_vModelCheckbox, consentToMedicalResearch.value]
                        ]),
                        _createElementVNode("span", _hoisted_22, _toDisplayString(_ctx.$t('platform.legal.privacy-policy.medical-research-consent')) + " (" + _toDisplayString(_ctx.$t('platform.common.optional')) + ") ", 1)
                      ])
                    ])
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }, 8, ["name", "steps", "current-step", "disabled"])
      ])
    ])
  ]))
}
}

})